<script setup lang="ts">
/* =============================================================
   RATE RULE — CREATE / EDIT
   =============================================================
   One rule of a berth's rate card, on its own page like every
   other record form here. A new rule is appended last -- the
   least specific until it is moved up on the Rates tab.

   The preview under the fields is the rule as the card will read
   it, so what is typed and what it means are on screen together.

   A copy of admin/src/pages/RateFormPage.vue, plus the check
   that the berth is the signed-in company's.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import TextField from '@/components/ui/TextField.vue'
import Alert from '@/components/ui/Alert.vue'
import { routes, to } from '@/config/routes'
import { getBerth } from '@/lib/berths'
import { createRate, getRate, updateRate } from '@/lib/rates'
import { rateConditionText, ratePriceText } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import { useUnsavedGuard } from '@/lib/useUnsavedGuard'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import { parseOptionalPositive } from '@/lib/numbers'
import type { BerthRateInput } from '@/lib/types'

const route = useRoute()
const router = useRouter()
const { user } = useAuth()

const berthId = computed(() => Number(route.params.id))
const rateId = computed(() => (route.params.rateId ? Number(route.params.rateId) : null))
const isEdit = computed(() => rateId.value !== null)
useDocumentTitle(() => (isEdit.value ? 'Edit rule' : 'New rule'))
const backTo = computed(() => to.berthTab(berthId.value, 'rates'))

interface FormState {
  /** Blank = any ship. */
  max_vessel_length_m: string
  /** Blank = any stay. */
  max_stay_hours: string
  price_eur: string
  /** Blank = flat per stay. */
  per_hours: string
}

const blank: FormState = { max_vessel_length_m: '', max_stay_hours: '', price_eur: '', per_hours: '24' }

const form = ref<FormState>({ ...blank })
const pristine = ref<FormState>({ ...blank })
const berthName = ref('')

const loading = ref(true)
const saving = ref(false)
const notFound = ref(false)
const formError = ref('')
const errors = ref<Record<string, string>>({})

const dirty = computed(() => JSON.stringify(form.value) !== JSON.stringify(pristine.value))
useUnsavedGuard(dirty, saving)

const blankIfNull = (n: number | null) => (n === null ? '' : String(n))

onMounted(async () => {
  try {
    const [berth, rate] = await Promise.all([
      getBerth(berthId.value),
      rateId.value === null ? Promise.resolve(null) : getRate(rateId.value),
    ])
    // A rule id that exists but belongs to another berth is not found here
    // either: the URL says which berth, and it should not be able to lie.
    // Nor is a berth of another company's -- readable, but not yours to price.
    const ownBerth = berth !== null && berth.company_id === user.value?.companyId
    if (!berth || !ownBerth || (isEdit.value && (!rate || rate.berth_id !== berthId.value))) {
      notFound.value = true
      return
    }
    berthName.value = berth.name
    if (rate) {
      const state: FormState = {
        max_vessel_length_m: blankIfNull(rate.max_vessel_length_m),
        max_stay_hours: blankIfNull(rate.max_stay_hours),
        price_eur: String(rate.price_eur),
        per_hours: blankIfNull(rate.per_hours),
      }
      form.value = { ...state }
      pristine.value = { ...state }
    }
  } catch (e) {
    formError.value = errorMessage(e, 'Could not load the rule.')
  } finally {
    loading.value = false
  }
})

/** Blank means "any" (or "flat"); a number must be positive. Mirrors the CHECKs. */
function validate(): BerthRateInput | null {
  const next: Record<string, string> = {}
  const len = parseOptionalPositive(form.value.max_vessel_length_m)
  if (len === undefined) next.max_vessel_length_m = 'A length in metres, or blank for any ship.'
  const hours = parseOptionalPositive(form.value.max_stay_hours)
  if (hours === undefined) next.max_stay_hours = 'A number of hours, or blank for any stay.'
  const per = parseOptionalPositive(form.value.per_hours, true)
  if (per === undefined) next.per_hours = 'Whole hours, or blank for a flat price per stay.'
  const rawPrice = form.value.price_eur.trim()
  const price = Number(rawPrice)
  if (rawPrice === '' || Number.isNaN(price) || price < 0) next.price_eur = 'A price in euros, zero or more.'

  errors.value = next
  if (len === undefined || hours === undefined || per === undefined || Object.keys(next).length > 0)
    return null
  return { max_vessel_length_m: len, max_stay_hours: hours, price_eur: price, per_hours: per }
}

/** The rule as the card will read it, live while it is typed. */
const preview = computed(() => {
  const input = {
    max_vessel_length_m: Number(form.value.max_vessel_length_m) || null,
    max_stay_hours: Number(form.value.max_stay_hours) || null,
    price_eur: Number(form.value.price_eur) || 0,
    per_hours: Number(form.value.per_hours) || null,
  }
  return `${rateConditionText(input)} → ${ratePriceText(input)}`
})

async function onSubmit(): Promise<void> {
  if (saving.value) return
  const input = validate()
  if (!input) return
  saving.value = true
  formError.value = ''
  try {
    if (rateId.value === null) await createRate(berthId.value, input)
    else await updateRate(rateId.value, input)
    pristine.value = { ...form.value }
    await router.push(backTo.value)
  } catch (e) {
    formError.value = errorMessage(e, 'Could not save the rule.')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <PageHeader
    :title="isEdit ? 'Edit rule' : 'New rule'"
    :description="
      berthName
        ? `${berthName} · the first rule that fits the ship and the stay prices the whole stay.`
        : undefined
    "
  />

  <p v-if="loading" class="page-status">Loading…</p>

  <Alert v-else-if="notFound" tone="danger">
    That rule or berth does not exist. <RouterLink :to="routes.locations">Back to your locations</RouterLink>.
  </Alert>

  <Panel v-else>
    <form class="form-layout" novalidate @submit.prevent="onSubmit">
      <Alert v-if="formError" tone="danger">{{ formError }}</Alert>

      <div class="form-grid">
        <TextField
          v-model="form.max_vessel_length_m"
          label="Ships up to (m)"
          :error="errors.max_vessel_length_m"
          hint="Blank = any ship."
          placeholder="Any ship"
          inputmode="decimal"
          autocomplete="off"
        />
        <TextField
          v-model="form.max_stay_hours"
          label="Stays up to (hours)"
          :error="errors.max_stay_hours"
          hint="Blank = any stay."
          placeholder="Any stay"
          inputmode="decimal"
          autocomplete="off"
        />
        <TextField
          v-model="form.price_eur"
          label="Price (EUR)"
          :error="errors.price_eur"
          hint="Net of VAT."
          placeholder="580"
          inputmode="decimal"
          autocomplete="off"
        />
        <TextField
          v-model="form.per_hours"
          label="Per (hours)"
          :error="errors.per_hours"
          hint="Every period started is charged in full. Blank = flat per stay."
          placeholder="Flat per stay"
          inputmode="numeric"
          autocomplete="off"
        />
      </div>

      <p class="preview">{{ preview }}</p>

      <footer class="form-actions">
        <Button variant="ghost" :to="backTo" :disabled="saving">Cancel</Button>
        <Button type="submit" :loading="saving">{{ isEdit ? 'Save rule' : 'Add rule' }}</Button>
      </footer>
    </form>
  </Panel>
</template>

<style scoped>
.preview {
  padding: var(--space-xs) var(--space-sm);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--color-heading);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-sm);
}
</style>
